// src/pages/auth/ForgotPasswordPage.tsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTheme } from "../../config/theme.config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTruckFast,
  faEnvelope,
  faLock,
  faEye,
  faEyeSlash,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import { customerRecoveryService } from "../../services/customer.recovery.service";
import Swal from "sweetalert2";
import useForm, { ValidationRules } from "../../hooks/useForm";

import styles from "../../styles/auth/AuthPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [formStep, setFormStep] = useState("enter-email"); // 'enter-email', 'enter-code', or 'reset-password'

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();

  const passwordValidationRules: ValidationRules<any> = {
    newPassword: (value) => {
      if (!value) return "La contraseña es requerida";
      if (value.length < 6) return "Debe tener al menos 6 caracteres";
      if (value.length > 16) return "Debe tener máximo 16 caracteres";
      if (!/[a-z]/.test(value)) return "Debe contener al menos una minúscula";
      if (!/[A-Z]/.test(value)) return "Debe contener al menos una mayúscula";
      if (!/[0-9]/.test(value)) return "Debe contener al menos un número";
      if (!/[^A-Za-z0-9]/.test(value))
        return "Debe contener al menos un carácter especial";
      return null;
    },
    confirmNewPassword: (value, values) => {
      if (!value) return "Confirma tu contraseña";
      if (value !== values.newPassword) return "Las contraseñas no coinciden";
      return null;
    },
  };

  const { values, errors, handleChange, handleSubmit } = useForm(
    {
      newPassword: "",
      confirmNewPassword: "",
    },
    passwordValidationRules
  );

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await customerRecoveryService.sendRecoveryCode(email);
      if (response.success) {
        setFormStep("enter-code");
      } else {
        const isUserNotFound = response.error && (response.error.toLowerCase().includes("no existe") || response.error.toLowerCase().includes("not exist"));
        Swal.fire({
          icon: "error",
          title: isUserNotFound ? "Usuario no encontrado" : "Error",
          text: isUserNotFound 
            ? "No encontramos una cuenta asociada a los datos ingresados. Verifica la información e inténtalo nuevamente."
            : (response.error || "Error al enviar email de recuperación"),
          confirmButtonText: "Aceptar",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ha ocurrido un error inesperado",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await customerRecoveryService.verifyRecoveryCode(
        email,
        code
      );
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Código Verificado",
          text: "Tu código ha sido verificado. Ahora puedes restablecer tu contraseña.",
        });
        setFormStep("reset-password"); // Move to the new password reset form
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.error || "Error al verificar el código",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ha ocurrido un error inesperado al verificar el código",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async () => {
    setLoading(true);

    try {
      const response = await customerRecoveryService.resetPassword(
        email,
        values.newPassword
      );
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Contraseña Restablecida",
          text: "Tu contraseña ha sido restablecida exitosamente. Ahora puedes iniciar sesión.",
        });
        navigate("/login");
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.error || "Error al restablecer la contraseña",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ha ocurrido un error inesperado al restablecer la contraseña",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authPage}>
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <div className={styles.authHeader}>
            <h2 className={styles.authTitle}>
              {formStep === "enter-email"
                ? "Recuperar Contraseña"
                : formStep === "enter-code"
                ? "Verificar Código"
                : "Nueva Contraseña"}
            </h2>
            <p className={styles.authSubtitle}>
              {formStep === "enter-email"
                ? "Te enviaremos un código para restablecer tu cuenta"
                : formStep === "enter-code"
                ? "Ingresa el código enviado a tu correo"
                : "Crea una nueva contraseña segura"}
            </p>
          </div>

          {formStep === "enter-email" ? (
            <form onSubmit={handleEmailSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.formLabel}>
                  Correo electrónico
                </label>
                <div className={styles.inputContainer}>
                  <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={styles.authFormInput}
                    placeholder="ejemplo@correo.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`${btnStyles.primary} ${btnStyles.fullWidth}`}
                style={{ marginTop: '16px' }}
              >
                {loading ? (
                  <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    Enviando...
                  </div>
                ) : (
                  <>
                    Enviar Código
                    <span className={styles.buttonArrow}>→</span>
                  </>
                )}
              </button>
            </form>
          ) : formStep === "enter-code" ? (
            <form onSubmit={handleCodeSubmit}>
              <div className={styles.formGroup}>
                <div className={styles.codeInputContainer}>
                  <input
                    id="code"
                    name="code"
                    type="text"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className={styles.codeInputHidden}
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                  />
                  <div className={styles.codeInputBoxes}>
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className={styles.codeInputBox}>
                        {code[i] || ""}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className={`${btnStyles.primary} ${btnStyles.fullWidth}`}
                style={{ marginTop: '16px' }}
              >
                {loading ? (
                  <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    Verificando...
                  </div>
                ) : (
                  <>
                    Verificar Código
                    <span className={styles.buttonArrow}>→</span>
                  </>
                )}
              </button>

              <div style={{ marginTop: "24px", fontSize: "13px", color: "#6b7280", textAlign: "center", lineHeight: "1.6" }}>
                <p style={{ fontWeight: "700", margin: "0 0 6px 0", color: "#374151" }}>
                  ¿No recibiste el código de verificación?
                </p>
                <p style={{ margin: 0 }}>
                  Por favor, revisa tu bandeja de entrada o correo no deseado (spam). Si aún no lo has recibido, haz clic en{" "}
                  <button
                    type="button"
                    onClick={handleEmailSubmit}
                    disabled={loading}
                    className={styles.authLink}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      fontWeight: "700",
                      textDecoration: "underline",
                      color: "#0a0a0a",
                      display: "inline",
                      font: "inherit"
                    }}
                  >
                    Reenviar código
                  </button>{" "}
                  para recibir uno nuevo.
                </p>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit(handleResetPasswordSubmit)}>
              <div className={styles.formGroup}>
                  <label htmlFor="newPassword" className={styles.formLabel}>
                    Nueva Contraseña
                  </label>
                  <div className={styles.inputContainer}>
                    <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
                    <input
                      id="newPassword"
                      name="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      required
                      value={values.newPassword}
                      onChange={handleChange}
                      maxLength={16}
                      className={`${styles.authFormInput} ${
                        errors.newPassword ? styles.isInvalid : ""
                      }`}
                      placeholder="Contraseña (A-Z, a-z, 0-9, @)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className={styles.inputButton}
                    >
                      <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p className={styles.invalidFeedback}>{errors.newPassword}</p>
                  )}
                </div>

                <div className={styles.formGroup} style={{ marginBottom: '24px' }}>
                  <label htmlFor="confirmNewPassword" className={styles.formLabel}>
                    Confirmar Contraseña
                  </label>
                  <div className={styles.inputContainer}>
                    <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
                    <input
                      id="confirmNewPassword"
                      name="confirmNewPassword"
                      type={showConfirmNewPassword ? "text" : "password"}
                      required
                      value={values.confirmNewPassword}
                      onChange={handleChange}
                      className={`${styles.authFormInput} ${
                        errors.confirmNewPassword ? styles.isInvalid : ""
                      }`}
                      placeholder="Confirma tu contraseña"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmNewPassword(!showConfirmNewPassword)
                      }
                      className={styles.inputButton}
                    >
                      <FontAwesomeIcon
                        icon={showConfirmNewPassword ? faEyeSlash : faEye}
                      />
                    </button>
                  </div>
                  {errors.confirmNewPassword && (
                    <p className={styles.invalidFeedback}>{errors.confirmNewPassword}</p>
                  )}
                </div>

              <button
                type="submit"
                disabled={loading}
                className={`${btnStyles.primary} ${btnStyles.fullWidth}`}
                style={{ marginTop: '16px' }}
              >
                {loading ? (
                  <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    Restableciendo...
                  </div>
                ) : (
                  <>
                    Restablecer Contraseña
                    <span className={styles.buttonArrow}>→</span>
                  </>
                )}
              </button>
            </form>
          )}

          <div className={styles.authFooter}>
            <Link to="/login" className={styles.backLink}>
              ← Volver al login
            </Link>
            <div className={styles.copyrightText}>
              © 2026 {theme.brand.name} un producto de EnviarLatam LLC.
            </div>
          </div>
        </div>

        <div className={styles.authSideMessage}>
          <div className={styles.sideMessageContent}>
            <div className={styles.messageBadge}>
              Seguridad
            </div>
            <h2>Protegemos tu cuenta</h2>
            <p>
              No te preocupes, recuperar el acceso es rápido y seguro. Sigue los pasos para restablecer tu contraseña y volver a disfrutar de nuestros servicios.
            </p>
            <div style={{ marginTop: '20px', opacity: 0.8, fontSize: '14px' }}>
              <FontAwesomeIcon icon={faTruckFast} style={{ marginRight: '10px' }} />
              Tu seguridad es nuestra prioridad.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
